/**
 * Debounced Subscription Manager
 * Manages real-time subscriptions with debouncing to prevent excessive connections
 */

export class SubscriptionManager {
  constructor(delay = 500) {
    this.subscriptions = new Map(); // key -> { unsubscribe, count, timeout }
    this.delay = delay;
  }

  /**
   * Subscribe with debouncing
   * @param {string} key - Unique subscription key
   * @param {Function} subscribeFunction - Function that returns unsubscribe callback
   * @param {Function} onData - Data callback
   */
  subscribe(key, subscribeFunction, onData) {
    const existing = this.subscriptions.get(key);

    if (existing) {
      // Clear any pending unsubscribe
      if (existing.timeout) {
        clearTimeout(existing.timeout);
        existing.timeout = null;
      }
      existing.count++;
      return;
    }

    // Create new subscription
    const unsubscribe = subscribeFunction(onData);
    this.subscriptions.set(key, {
      unsubscribe,
      count: 1,
      timeout: null
    });
  }

  /**
   * Unsubscribe with debouncing
   * @param {string} key - Subscription key
   */
  unsubscribe(key) {
    const existing = this.subscriptions.get(key);
    if (!existing) return;

    existing.count--;

    if (existing.count <= 0) {
      // Debounce the actual unsubscribe
      existing.timeout = setTimeout(() => {
        if (existing.unsubscribe) {
          existing.unsubscribe();
        }
        this.subscriptions.delete(key);
      }, this.delay);
    }
  }

  /**
   * Force cleanup all subscriptions
   */
  cleanup() {
    this.subscriptions.forEach(({ unsubscribe, timeout }) => {
      if (timeout) clearTimeout(timeout);
      if (unsubscribe) unsubscribe();
    });
    this.subscriptions.clear();
  }
}
